import React, {Suspense} from 'react'
import PageLayout from '@view/layout/pageLayout/PageLayout'
import {Box, Button, Stack, Typography} from '@mui/material'
import ShowChartIcon from '@mui/icons-material/ShowChart'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import SummarizeIcon from '@mui/icons-material/Summarize'
import MapIcon from '@mui/icons-material/Map'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import VideocamIcon from '@mui/icons-material/Videocam'
import SpinnerCustom from '@ui/spinner/SpinnerCustom'
import PhotoFixationByAccount from '@containers/monitoringPlatform/photoFixation/PhotoFixationByAccount'
import RecordingsByAccount from '@containers/monitoringPlatform/geoZones/RecordingsByAccount'
import VideoStreamsByAccount from '@containers/video/videoStreams/videoStreamsByAccount/VideoStreamsByAccount'
import PassengerCharts from '@containers/monitoringPlatform/passengerCharts/PassengerCharts'
import PassengerVerification from '@containers/monitoringPlatform/passengerVerification/PassengerVerification'
import {useQueryState} from '@hooks/query/useQueryState'
import AccountAccentScope from '@containers/accountStyle/scope/AccountAccentScope'
import sx from '@pages/monitoringPlatform/sx'

type Tab = 'graphics' | 'verification' | 'reports' | 'geoZones' | 'photoFixation' | 'streams'

const TABS: {key: Tab; label: string; icon: React.ReactNode}[] = [
	{key: 'graphics', label: 'Графіки', icon: <ShowChartIcon/>},
	{key: 'verification', label: 'Верифікація', icon: <FactCheckIcon/>},
	{key: 'reports', label: 'Звіти', icon: <SummarizeIcon/>},
	{key: 'geoZones', label: 'Модуль геозон', icon: <MapIcon/>},
	{key: 'photoFixation', label: 'Фотофіксація', icon: <PhotoCameraIcon/>},
	{key: 'streams', label: 'Онлайн трансляція', icon: <VideocamIcon/>},
]

const MonitoringPlatformPage = () => {
	const [tab, setTab] = useQueryState<Tab>('mpTab', 'graphics', {
		parse: (value) => (value as Tab) || 'graphics'
	})

	return (
		<PageLayout>
			<AccountAccentScope>
				<Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
					<Stack
						direction="row"
						justifyContent="center"
						alignItems="center"
						mb={2}
						flexShrink={0}
						position="relative"
					>
						<Typography variant="h6" sx={sx.title}>
							Платформа інтелектуального моніторингу
						</Typography>
					</Stack>

					<Box sx={sx.tabs}>
						{TABS.map(({key, label, icon}) => (
							<Button
								key={key}
								size="small"
								className={tab === key ? 'active' : undefined}
								sx={sx.tab}
								onClick={() => setTab(key)}
							>
								<Box component="span" sx={sx.tabIconWrap}>{icon}</Box>
								{label}
							</Button>
						))}
					</Box>

					<Box sx={{flex: 1, overflow: 'auto'}}>
						{tab === 'graphics' && (
							<Suspense fallback={<SpinnerCustom/>}>
								<PassengerCharts/>
							</Suspense>
						)}

						{tab === 'verification' && (
							<Suspense fallback={<SpinnerCustom/>}>
								<PassengerVerification/>
							</Suspense>
						)}

						{tab === 'reports' && (
							<Typography textAlign="center" color="text.secondary">
								Звіти
							</Typography>
						)}

						{tab === 'geoZones' && (
							<Suspense fallback={<SpinnerCustom/>}>
								<RecordingsByAccount/>
							</Suspense>
						)}

						{tab === 'photoFixation' && (
							<Suspense fallback={<SpinnerCustom/>}>
								<PhotoFixationByAccount/>
							</Suspense>
						)}

						{tab === 'streams' && (
							<Suspense fallback={<SpinnerCustom/>}>
								<VideoStreamsByAccount/>
							</Suspense>
						)}
					</Box>
				</Box>
			</AccountAccentScope>
		</PageLayout>
	)
}

export default MonitoringPlatformPage
